<template>
  <div class="root">
    <div class="body">
      <vue-loading v-if="isLoading" type="spiningDubbles" color="#efad4c" :size="{ width: '50px', height: '50px' }" />

      <template v-else>
        <div class="contest-list">
          <div v-if="displayContests.length" class="card-list">
            <contest-card v-for="contest in displayContests" :key="contest.id" :contest="contest" />
          </div>
          <div v-else class="empty-card">{{ $t("eventContest.noContests") }}</div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { VueLoading } from "vue-loading-template";
import ContestCard from "@/components/molecules/ContestCard.vue";
import axiosBase from "axios";
import { showNotifyDialog } from "@/lib/dialog";
import { retryableGet } from "@/lib/utils/networkRetry";
import orderBy from "lodash/orderBy";

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { VueLoading, ContestCard },
  data() {
    return {
      isLoading: true,
      contests: [],
    };
  },
  async created() {
    // microCMS では 2023/10/2 以降に作成されたサービスに limit の上限 100 件の制限があるが、
    // Nola のサービスは後方互換の対象（少なくとも 2021/2/20 以前に作成）で制限が有効化されていないため、
    // 1 リクエストで 100 件を超える取得が可能。そのためページネーションせず 1000 件を指定している。
    // ただしレスポンスサイズの上限はあるため、件数が大きく増える場合は offset での分割取得を検討する。
    const contestRequestUrl = "/contest?filters=isRegularTheme[equals]false&limit=1000";
    const contestRequest = retryableGet(axios, contestRequestUrl);

    const result = await Promise.all([contestRequest]);

    const contest = result.find((x) => x.config.url === contestRequestUrl)!;

    if (contest.status !== 200) {
      await showNotifyDialog({
        title: this.$t("eventContest.error.title"),
        content: this.$t("eventContest.error.fetchFailed"),
      });
    }

    this.contests = contest.data.contents;
    this.isLoading = false;
  },
  methods: {
    // コンテストの締切日までの残り日数を計算 (締切日が過ぎている場合は-1を返す)
    calculateDaysUntilDeadline(deadline) {
      if (!deadline) {
        return -1;
      }
      const now = new Date();
      const deadlineDateTime = new Date(deadline);
      // ミリ秒単位の差を日数に変換
      const inDays = Math.floor((deadlineDateTime.getTime() - +now) / (1000 * 60 * 60 * 24));
      // 同日内だが既に時間が過ぎている場合
      if (inDays === 0 && now > deadlineDateTime) {
        return -1;
      }
      return inDays;
    },
  },
  computed: {
    // 締切までの日数が 0 以上の募集中コンテスト（microCMSの並び順を維持）
    availableContests() {
      return this.contests.filter((contest: any) => this.calculateDaysUntilDeadline(contest.deadline) >= 0);
    },
    // 締切が過ぎたコンテスト（降順：最近締切のものが先頭）
    closedContests() {
      const closed = this.contests.filter((contest: any) => this.calculateDaysUntilDeadline(contest.deadline) < 0);
      return orderBy(closed, [(contest) => new Date(contest.deadline).getTime()], ["desc"]);
    },
    // 表示用に募集中コンテストの後に終了コンテストを連結
    displayContests() {
      return [...this.availableContests, ...this.closedContests];
    },
  },
});

interface Props {}

interface Data {
  isLoading: boolean;
  contests: Contest[];
}

interface Computed {
  availableContests: Contest[];
  closedContests: Contest[];
  displayContests: Contest[];
}

interface Methods {
  calculateDaysUntilDeadline: (deadline: string) => number;
}

type Contest = {
  [key: string]: any;
};
</script>

<style lang="scss" scoped>
.root {
  height: calc(100vh - #{$header-height});
  background-color: var(--color-surface-muted);
  display: flex;
  flex-direction: column;

  @include mq("sp") {
    height: 100%;
    min-height: 0;
  }
}

.body {
  box-sizing: border-box;
  padding: 30px;
  overflow: auto;

  .contest-list {
    .card-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, 280px);
      gap: 30px;
      margin: 2rem 0;
    }

    .empty-card {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 30px;
      font-weight: var(--font-weight-bold);
      font-size: 1.2em;
      color: var(--color-text-normal);
    }
  }
}
</style>
